import { RestError } from '@azure/core-http';
import { useImmer } from 'use-immer';
import { useMutationAddNote } from './add-note.queries';
import { useHasPermissions } from '../../../../core/permissions/has-permissions.hook';
import { Permissions } from '../../../../core/permissions/permissions.typings';

export enum KnownNoteEntityTypes {
  Delivery = 'delivery',
  Order = 'order',
  User = 'user',
  Group = 'group',
  Courier = 'courier'
}

export interface AddNoteActionProps {
  id: string;
  projectID: string;
  entityType: KnownNoteEntityTypes;
  onAbort(): void;
  onSuccess(): void;
}

export interface AddNoteActionState {
  isLoading: boolean;
  isPrivate: boolean;
  error?: RestError;
}

export interface AddNoteActionHandlers {
  // onPrivate(isPrivate: AddNoteActionState['isPrivate']): void;
  onAddNote(content: string, isPrivate: boolean): void;
  onCancel(): void;
}

const initialState: AddNoteActionState = {
  isLoading: false,
  isPrivate: true
};

export function useAddNoteAction({ onAbort, onSuccess, id, entityType, projectID }: AddNoteActionProps) {
  const hasCreatePrivateNotePermissions = useHasPermissions([
    { action: Permissions.notes.createPrivate, projectID: projectID }
  ]);
  const [state, setState] = useImmer<AddNoteActionState>(initialState);
  const addNoteMutation = useMutationAddNote(projectID);

  const handlers: AddNoteActionHandlers = {
    onAddNote(content, isPrivate) {
      setState((state) => {
        state.isLoading = true;
      });
      addNoteMutation.mutate(
        {
          id,
          entityType,
          content,
          isPrivate
        },
        {
          onSuccess() {
            setState((state) => {
              state.isLoading = false;
            });
            onSuccess();
          },
          onError() {
            setState((state) => {
              state.isLoading = false;
            });
            throw Error(`There was an issue with creating this note`);
          }
        }
      );
    },
    onCancel() {
      onAbort();
    }
  };

  return {
    state,
    isLoading: state.isLoading || hasCreatePrivateNotePermissions.isLoading,
    handlers,
    hasCreatePrivateNotePermissions: hasCreatePrivateNotePermissions.hasSomePermissions
  } as const;
}
